import React, {FC, PropsWithChildren, useCallback, useMemo} from 'react';

import {
  KeyboardContext,
  KeyboardContextProps,
  KeyboardListenerConfiguration,
  useKeyboardContext,
} from './keyboardContext';

/*
 * Props.
 */

interface KeyboardShortcutsFilterProps {
  /** Whether to enable keyboard shortcuts in descendants. */
  isEnabled: boolean;
}

/*
 * Component.
 */

export const KeyboardShortcutsFilter: FC<PropsWithChildren<KeyboardShortcutsFilterProps>> = ({
  isEnabled,
  children,
}) => {
  const context = useKeyboardContext();

  const filterConfiguration = useCallback(
    (configuration: KeyboardListenerConfiguration): KeyboardListenerConfiguration => ({
      ...configuration,
      handlers: isEnabled ? configuration.handlers : {},
    }),
    [isEnabled],
  );

  const filteredContext: KeyboardContextProps = useMemo(
    () => ({
      ...context,
      registerListener: (configuration) => {
        const updateListener = context.registerListener(filterConfiguration(configuration));

        return (newConfiguration) =>
          updateListener(newConfiguration && filterConfiguration(newConfiguration));
      },
    }),
    [context, filterConfiguration],
  );

  return <KeyboardContext.Provider value={filteredContext}>{children}</KeyboardContext.Provider>;
};
